import { supabase } from "@/integrations/supabase/client";
import type { Json } from "@/integrations/supabase/types";

export interface LogEntry {
  action: string;
  entity_type: string;
  entity_id?: string;
  details?: Json;
  ip_address?: string;
  user_agent?: string;
}

/**
 * Logs an admin action to the admin_logs table
 * IMPORTANT: must never block core user flows (auth, navigation).
 */
export async function logAdminAction(entry: LogEntry): Promise<void> {
  // Fire-and-forget: return immediately
  setTimeout(async () => {
    try {
      // Use cached session (no network) rather than getUser() (network)
      const { data: { session } } = await supabase.auth.getSession();

      const logData = {
        user_id: session?.user?.id || null,
        action: entry.action,
        entity_type: entry.entity_type,
        entity_id: entry.entity_id || null,
        details: entry.details || null,
        ip_address: entry.ip_address || null,
        user_agent: entry.user_agent || navigator.userAgent,
      };

      const { error } = await supabase
        .from('admin_logs')
        .insert([logData]);

      if (error) {
        console.error('Failed to log admin action:', error);
      }
    } catch (err) {
      console.error('Error logging admin action:', err);
    }
  }, 0);
}

/**
 * Notifies all admin users about a critical action
 */
async function notifyAdmins(actionType: string, details: Record<string, unknown>): Promise<void> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    // Get performer's email
    let performerEmail = 'Unknown';
    if (user?.id) {
      const { data: profile } = await supabase
        .from('profiles')
        .select('email')
        .eq('id', user.id)
        .single();
      performerEmail = profile?.email || user.email || 'Unknown';
    }

    await supabase.functions.invoke('notify-admin-action', {
      body: {
        action_type: actionType,
        action_details: {
          ...details,
          performed_by_email: performerEmail,
        },
      },
    });
  } catch (err) {
    console.error('Error notifying admins:', err);
    // Don't throw - notification failures shouldn't block the action
  }
}

/**
 * Pre-defined logging functions for common actions
 */
export const AdminLogger = {
  roleChange: async (userId: string, oldRole: string | null, newRole: string, targetEmail?: string) => {
    await logAdminAction({
      action: 'role_change',
      entity_type: 'user',
      entity_id: userId,
      details: {
        old_role: oldRole,
        new_role: newRole,
        target_email: targetEmail,
      },
    });

    // Notify admins about role changes
    await notifyAdmins('role_change', {
      target_user_email: targetEmail,
      target_user_id: userId,
      old_value: oldRole,
      new_value: newRole,
    });
  },

  claimStatusChange: async (claimId: string, oldStatus: string, newStatus: string, claimNumber?: string) => {
    await logAdminAction({
      action: 'status_change',
      entity_type: 'claim',
      entity_id: claimId,
      details: {
        old_status: oldStatus,
        new_status: newStatus,
        claim_number: claimNumber,
      },
    });
  },

  broadcastNotification: async (broadcastId: string, title: string, targetRoles: string[] | null, sentCount: number) => {
    await logAdminAction({
      action: 'broadcast_sent',
      entity_type: 'broadcast',
      entity_id: broadcastId,
      details: {
        title,
        target_roles: targetRoles,
        sent_count: sentCount,
      },
    });

    // Notify admins about broadcasts
    await notifyAdmins('broadcast_sent', {
      additional_info: title,
      new_value: targetRoles ? targetRoles.join(', ') : 'All users',
    });
  },

  userCreated: async (userId: string, email: string, role: string) => {
    await logAdminAction({
      action: 'user_created',
      entity_type: 'user',
      entity_id: userId,
      details: {
        email,
        role,
      },
    });
  },

  userDeleted: async (userId: string, email?: string) => {
    await logAdminAction({
      action: 'user_deleted',
      entity_type: 'user',
      entity_id: userId,
      details: {
        email,
      },
    });

    // Notify admins about user deletion
    await notifyAdmins('user_deleted', {
      target_user_email: email,
      target_user_id: userId,
    });
  },

  login: async () => {
    await logAdminAction({
      action: 'login',
      entity_type: 'auth',
    });
  },

  logout: async () => {
    await logAdminAction({
      action: 'logout',
      entity_type: 'auth',
    });
  },

  claimCreated: async (claimNumber: string, claimType: string, policyholderName: string, adjusterName: string) => {
    await logAdminAction({
      action: 'claim_created',
      entity_type: 'claim',
      entity_id: claimNumber,
      details: {
        claim_number: claimNumber,
        claim_type: claimType,
        policyholder_name: policyholderName,
        adjuster_name: adjusterName,
      },
    });
  },

  claimUpdated: async (claimId: string, claimNumber: string, changes?: Record<string, Json>) => {
    await logAdminAction({
      action: 'claim_updated',
      entity_type: 'claim',
      entity_id: claimId,
      details: {
        claim_number: claimNumber,
        changes: changes || null,
      },
    });
  },

  impersonationStart: async (targetUserId: string, targetEmail: string, targetRoles: string[]) => {
    await logAdminAction({
      action: 'impersonation_start',
      entity_type: 'user',
      entity_id: targetUserId,
      details: {
        target_email: targetEmail,
        target_roles: targetRoles,
      },
    });

    await notifyAdmins('impersonation_start', {
      target_user_email: targetEmail,
      target_user_id: targetUserId,
      additional_info: `Roles: ${targetRoles.join(', ')}`,
    });
  },

  impersonationStop: async (targetUserId: string, targetEmail: string) => {
    await logAdminAction({
      action: 'impersonation_stop',
      entity_type: 'user',
      entity_id: targetUserId,
      details: {
        target_email: targetEmail,
      },
    });
  },
};
